import { useEffect, useState } from "react";
import type { LoyaltySummary } from "@baluu/ui/lib/checkout/loyalty";
import { apiOrigin } from "~/widgets/lib/basket-validate";

type Params = {
  apiUrl: string;
  businessId: string;
  /** The signed-in customer's bearer token; null when signed out. */
  authToken: string | null | undefined;
};

/**
 * The signed-in customer's loyalty standing at this school
 * (`GET /widget/loyalty/me`, identity from the bearer), as the widget's
 * basket checkout reads it (apps/widget/.../LoyaltySection). Null whenever
 * there is nothing to show — signed out, no program, a failed lookup — so
 * the checkout block simply doesn't render: a signed-out buyer gets no nudge,
 * since there is no way to know whether this school runs a program at all.
 */
export function useBasketLoyalty({
  apiUrl,
  businessId,
  authToken,
}: Params): LoyaltySummary | null {
  // Kept with the question it answers: a sign-out or another account reads
  // as null at once, never as the previous customer's points.
  const key = authToken ? `${apiUrl}|${businessId}|${authToken}` : null;
  const [answer, setAnswer] = useState<{
    key: string;
    summary: LoyaltySummary | null;
  } | null>(null);

  useEffect(() => {
    if (!key || !authToken) return;
    let cancelled = false;
    const settle = (summary: LoyaltySummary | null) => {
      if (!cancelled) setAnswer({ key, summary });
    };
    fetch(
      `${apiOrigin(apiUrl)}/widget/loyalty/me?schoolId=${encodeURIComponent(businessId)}`,
      { headers: { Authorization: `Bearer ${authToken}` } },
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => settle(isLoyaltySummary(data) ? data : null))
      .catch(() => settle(null));
    return () => {
      cancelled = true;
    };
  }, [apiUrl, businessId, authToken, key]);

  const summary = key && answer?.key === key ? answer.summary : null;
  return summary;
}

/** An answer with nothing to show is `null` (or a body without a program);
 *  only a real standing renders the block. */
function isLoyaltySummary(data: unknown): data is LoyaltySummary {
  return (
    !!data &&
    typeof data === "object" &&
    typeof (data as LoyaltySummary).pointsBalance === "number" &&
    typeof (data as LoyaltySummary).programStatus === "string"
  );
}
